import { MaterialCommunityIcons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { useMutation, useQuery } from '@apollo/client/react';
import * as Linking from 'expo-linking';
import { useRouter } from 'expo-router';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Platform, Pressable, RefreshControl, ScrollView, StyleSheet, View } from 'react-native';
import { Chip, HelperText, Text, TouchableRipple } from 'react-native-paper';

import { AppTextInput } from '@/components/atoms';
import { ErrorState, LoadingState } from '@/components/atoms/state';

import {
  ActionLinkRow,
  type ActionLinkItem,
} from '@/components/molecules/action-link/ActionLinkRow';
import { MeetingSummarySheet } from '@/components/molecules/meetings/MeetingSummarySheet';
import { UserPickerSheet, type UserOption } from '@/components/molecules/person-in-charge';
import { USER_PICKER_GET_USERS_VARIABLES } from '@/codegen/usermobile-bff/users/userPickerGetUsersVariables';
import {
  ContactPickerDialog,
  type PickerSelectedContact,
} from '@/components/organisms/contacts/ContactPickerDialog';
import { MeetingDateTimePickerDialog } from '@/components/molecules/meetings/MeetingDateTimePickerDialog';
import { MeetingRecordingPrompt } from '@/components/molecules/meetings/MeetingRecordingPrompt';
import { MAX_MEETING_MANUAL_MEMO_LENGTH } from '@/constants/meetingMemo';
import { MAX_MEETING_TITLE_LENGTH } from '@/constants/meetingTitle';
import { formatDate } from '@/formatters/formatDate';
import {
  GetMeetingDocument,
  GetUsersDocument,
  MeetingRecordingSummaryStatus,
  UpdateMeetingDocument,
  type GetMeetingQuery,
} from '@/generated/usermobile-bff/graphql';
import { useRefreshHandler } from '@/hooks';
import { useTranslation } from '@/i18n';
import { useGlobalAlertStore } from '@/stores/useGlobalAlertStore';
import { useMeetingAnalyticsStore } from '@/stores/useMeetingAnalyticsStore';
import { useVoiceRecordingStore } from '@/stores/useVoiceRecordingStore';
import { rippleOnLink, rippleOnSurface, unisonColors, useAppTheme } from '@/theme';
import type {
  ContactPickerCloseAction,
  ContactPickerSource,
} from '@/models/contactPickerAnalytics';
import { getMeetingRecordingUiState } from '@/models/meetingRecordingEligibility';

type Meeting = GetMeetingQuery['meeting'];

interface MeetingFormValues {
  readonly title: string;
  readonly notes: string;
  readonly startsAt: string;
  readonly attendees: readonly PickerSelectedContact[];
}

interface MeetingDetailProps {
  readonly id: string;
  readonly saveHandlerRef?: React.MutableRefObject<(() => void) | null>;
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly initialPickerVisible?: boolean;
  readonly onSaved?: () => void;
}

const styles = StyleSheet.create({
  attendeesChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingBottom: 8,
  },
  attendeesContainer: {
    paddingVertical: 8,
  },
  attendeesFieldBody: {
    flex: 1,
  },
  attendeesFieldContainer: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  attendeesFieldContent: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    minHeight: 48,
    paddingBottom: 8,
  },
  attendeesFieldIcon: {
    marginTop: 24,
  },
  attendeesFieldUnderline: {
    borderBottomColor: unisonColors.borderNdBase,
    borderBottomWidth: 1,
  },
  attendeesLabel: {
    color: unisonColors.textNdSecondary,
    fontSize: 12,
    letterSpacing: 0.4,
    lineHeight: 16,
    marginBottom: 4,
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  divider: {
    borderBottomColor: unisonColors.borderNdBase,
    borderBottomWidth: 1,
  },
  fieldLabel: {
    color: unisonColors.textNdSecondary,
    fontSize: 12,
    letterSpacing: 0.4,
    lineHeight: 16,
  },
  fieldValue: {
    color: unisonColors.textNdPrimary,
    fontSize: 16,
    letterSpacing: 0.5,
    lineHeight: 24,
  },
  fieldValueError: {
    color: unisonColors.onErrorContainer,
  },
  fieldTouchable: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  linkRow: {
    flexDirection: 'row',
    paddingRight: 16,
  },
  linkTouchable: {
    borderRadius: 4,
    overflow: 'hidden',
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  linkText: {
    color: unisonColors.onInfoContainer,
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: 0.1,
    lineHeight: 20,
  },
  summaryContainer: {
    paddingTop: 16,
  },
  summaryContent: {
    paddingHorizontal: 16,
    paddingVertical: 4,
  },
  textInput: {
    backgroundColor: 'transparent',
  },
});

const getSummaryText = (meeting: Meeting): string => meeting.recording?.summary?.summaryText ?? '';

const pendingUploadMeetingIdsSelector = (state: {
  upload: { pendingUploadMeetingIds: readonly string[] };
}) => state.upload.pendingUploadMeetingIds;

const failedUploadMeetingIdsSelector = (state: {
  upload: { failedUploadMeetingIds: readonly string[] };
}) => state.upload.failedUploadMeetingIds;

interface SummaryDisplay {
  readonly text: string;
  readonly isError: boolean;
  readonly showCompletedLinks: boolean;
  readonly showWifiActions: boolean;
}

const getQuotaResetDate = (locale: string): string => {
  const now = new Date();
  const nextMonthFirst = new Date(now.getFullYear(), now.getMonth() + 1, 1);

  return new Intl.DateTimeFormat(locale, { month: 'long', day: '2-digit' }).format(nextMonthFirst);
};

const getSummaryDisplay = ({
  hasRecording,
  hasPendingUpload,
  hasFailedUpload,
  isScheduledWithoutRecording,
  summaryStatus,
  summaryText,
  quotaResetDate,
  t,
}: {
  readonly hasRecording: boolean;
  readonly hasPendingUpload: boolean;
  readonly hasFailedUpload: boolean;
  readonly isScheduledWithoutRecording: boolean;
  readonly summaryStatus: MeetingRecordingSummaryStatus | null | undefined;
  readonly summaryText: string;
  readonly quotaResetDate: string;
  readonly t: (key: string, options?: Record<string, unknown>) => string;
}): SummaryDisplay => {
  if (hasPendingUpload) {
    return {
      text: t('meetings.detail.summaryWifiUnavailable'),
      isError: true,
      showCompletedLinks: false,
      showWifiActions: true,
    };
  }

  if (hasFailedUpload) {
    return {
      text: t('meetings.detail.summaryUploadFailed'),
      isError: true,
      showCompletedLinks: false,
      showWifiActions: false,
    };
  }

  if (summaryStatus === 'COMPLETED') {
    if (summaryText) {
      return {
        text: summaryText,
        isError: false,
        showCompletedLinks: true,
        showWifiActions: false,
      };
    }

    return {
      text: t('meetings.detail.summaryNoSpeechDetected'),
      isError: false,
      showCompletedLinks: false,
      showWifiActions: false,
    };
  }

  if (summaryStatus === 'PENDING' || summaryStatus === 'PROCESSING') {
    return {
      text: t('meetings.detail.summaryInProgress'),
      isError: false,
      showCompletedLinks: false,
      showWifiActions: false,
    };
  }

  if (summaryStatus === 'ON_HOLD') {
    return {
      text: t('meetings.detail.summaryQuotaExceeded', { date: quotaResetDate }),
      isError: true,
      showCompletedLinks: false,
      showWifiActions: false,
    };
  }

  if (summaryStatus === 'FAILED') {
    return {
      text: t('meetings.detail.summaryFailed'),
      isError: true,
      showCompletedLinks: false,
      showWifiActions: false,
    };
  }

  if (!hasRecording) {
    if (isScheduledWithoutRecording) {
      return {
        text: t('meetings.detail.scheduledRecordingCta'),
        isError: false,
        showCompletedLinks: false,
        showWifiActions: false,
      };
    }

    return {
      text: t('meetings.detail.summaryNotAvailableImported'),
      isError: false,
      showCompletedLinks: false,
      showWifiActions: false,
    };
  }

  return {
    text: '',
    isError: false,
    showCompletedLinks: false,
    showWifiActions: false,
  };
};

const toPickerSelectedContacts = (
  attendees: Meeting['attendees'],
  fallback: string,
): readonly PickerSelectedContact[] =>
  attendees.map((a) => ({
    id: Number(a.attendeeId),
    name: a.contact?.displayName ?? fallback,
  }));

interface AttendeeChipProps {
  readonly attendee: PickerSelectedContact;
  readonly onRemove: (contactId: number) => void;
}

const AttendeeChip = ({ attendee, onRemove }: AttendeeChipProps) => {
  const handleClose = useCallback(() => {
    onRemove(attendee.id);
  }, [attendee.id, onRemove]);

  return (
    <Chip mode="outlined" onClose={handleClose} testID={`attendee-chip-${attendee.id}`}>
      {attendee.name}
    </Chip>
  );
};

export const MeetingDetail = ({
  id,
  saveHandlerRef,
  onDirtyChange,
  initialPickerVisible = false,
  onSaved,
}: MeetingDetailProps) => {
  const { t, locale } = useTranslation();
  const theme = useAppTheme();
  const router = useRouter();
  const showGlobalAlert = useGlobalAlertStore((state) => state.showGlobalAlert);
  const trackContactPickerShown = useMeetingAnalyticsStore(
    (state) => state.trackContactPickerShown,
  );
  const trackContactPickerClosed = useMeetingAnalyticsStore(
    (state) => state.trackContactPickerClosed,
  );
  const trackContactAttached = useMeetingAnalyticsStore((state) => state.trackContactAttached);
  const pendingUploadMeetingIds = useVoiceRecordingStore(pendingUploadMeetingIdsSelector);
  const failedUploadMeetingIds = useVoiceRecordingStore(failedUploadMeetingIdsSelector);

  const { data, loading, error, refetch } = useQuery(GetMeetingDocument, {
    variables: { id },
  });
  const { onRefresh, refreshing } = useRefreshHandler(refetch);

  const meeting = data?.meeting;
  const recordingUiState = meeting ? getMeetingRecordingUiState(meeting) : 'hidden';
  const showRecordingPrompt = recordingUiState !== 'hidden';
  const summaryText = meeting ? getSummaryText(meeting) : '';
  const summaryStatus = meeting?.recording?.summary?.status;
  const hasPendingUpload = pendingUploadMeetingIds.includes(id);
  const hasFailedUpload = failedUploadMeetingIds.includes(id);
  const quotaResetDate = getQuotaResetDate(locale);
  const summaryDisplay = getSummaryDisplay({
    hasRecording: Boolean(meeting?.recording),
    hasPendingUpload,
    hasFailedUpload,
    isScheduledWithoutRecording: recordingUiState === 'start_cta',
    summaryStatus,
    summaryText,
    quotaResetDate,
    t,
  });

  const unknownContactFallback = t('meetings.detail.unknownContact', {
    defaultValue: 'Unknown Contact',
  });

  const meetingFormValues = useMemo((): MeetingFormValues => {
    const startsAt = meeting?.startsAt ?? '';
    const name = meeting?.name;
    const title =
      name != null && name !== ''
        ? name.slice(0, MAX_MEETING_TITLE_LENGTH)
        : startsAt
          ? formatDate(startsAt, locale)
          : '';

    return {
      title,
      notes: meeting?.notes ?? '',
      startsAt,
      attendees: meeting ? toPickerSelectedContacts(meeting.attendees, unknownContactFallback) : [],
    };
  }, [locale, meeting, unknownContactFallback]);

  const { clearErrors, control, formState, getValues, handleSubmit, reset, setError, setValue } =
    useForm<MeetingFormValues>({
      defaultValues: { title: '', notes: '', startsAt: '', attendees: [] },
    });

  const meetingTitleSyncKey = meeting
    ? `${meeting.id}:${meeting.name ?? ''}:${meeting.startsAt}`
    : '';

  useEffect(() => {
    if (!meetingTitleSyncKey) {
      return;
    }

    reset(meetingFormValues, { keepDirtyValues: true });
  }, [meetingTitleSyncKey, meetingFormValues, reset]);

  const [pickerVisible, setPickerVisible] = useState(initialPickerVisible);
  const [pickerAutoOpened, setPickerAutoOpened] = useState(initialPickerVisible);
  const pickerSource: ContactPickerSource = initialPickerVisible
    ? 'recording_flow'
    : 'meeting_detail';
  const recordingSessionId = meeting?.recording?.uuid ?? null;
  const [dateTimePickerVisible, setDateTimePickerVisible] = useState(false);
  const [summarySheetVisible, setSummarySheetVisible] = useState(false);
  const [userPickerVisible, setUserPickerVisible] = useState(false);
  const [selectedUserUid, setSelectedUserUid] = useState<string | null>(null);
  const [isUserDirty, setIsUserDirty] = useState(false);
  const [hoveredField, setHoveredField] = useState<string | null>(null);

  const { data: usersData, loading: usersLoading } = useQuery(GetUsersDocument, {
    variables: USER_PICKER_GET_USERS_VARIABLES,
    fetchPolicy: 'cache-and-network',
  });

  const userOptions = useMemo<readonly UserOption[]>(
    () => (usersData?.users.data ?? []).map((user) => ({ id: user.id, label: user.name })),
    [usersData?.users.data],
  );

  const selectedUserId = useMemo(() => {
    if (selectedUserUid) {
      const user = usersData?.users.data.find((u) => u.uid === selectedUserUid);
      return user?.id ?? null;
    }
    return meeting?.user?.id ?? null;
  }, [selectedUserUid, meeting?.user?.id, usersData?.users.data]);

  const selectedUserName = useMemo(() => {
    if (selectedUserUid) {
      const user = usersData?.users.data.find((u) => u.uid === selectedUserUid);
      return user?.name ?? '';
    }
    return meeting?.user?.name ?? '';
  }, [selectedUserUid, meeting?.user?.name, usersData?.users.data]);

  const handleSelectUser = useCallback(
    (userId: number | null) => {
      setUserPickerVisible(false);
      if (userId == null) return;
      const user = usersData?.users.data.find((u) => u.id === userId);
      if (!user) return;
      setSelectedUserUid(user.uid);
      setIsUserDirty(true);
    },
    [usersData?.users.data],
  );

  const localStartsAt = useWatch({ control, name: 'startsAt' });
  const localAttendees = useWatch({ control, name: 'attendees' });
  const formattedDate = localStartsAt ? formatDate(localStartsAt, locale) : '';

  const { isDirty, dirtyFields } = formState;
  const isFormDirty = isDirty || isUserDirty;

  useEffect(() => {
    onDirtyChange?.(isFormDirty);
  }, [isFormDirty, onDirtyChange]);

  const memoMaxLengthMessage = useMemo(
    () =>
      t('meetings.detail.memoMaxLength', {
        max: MAX_MEETING_MANUAL_MEMO_LENGTH,
        defaultValue: `Memo must be at most ${MAX_MEETING_MANUAL_MEMO_LENGTH} characters`,
      }),
    [t],
  );

  const [updateMeeting] = useMutation(UpdateMeetingDocument, {
    refetchQueries: [{ query: GetMeetingDocument, variables: { id } }],
  });

  const onSubmit = useCallback(
    async (data: MeetingFormValues) => {
      if (data.notes.length > MAX_MEETING_MANUAL_MEMO_LENGTH) {
        setError('notes', { type: 'maxLength', message: memoMaxLengthMessage });
        return;
      }

      try {
        await updateMeeting({
          variables: {
            input: {
              id,
              ...(dirtyFields.title ? { name: data.title } : {}),
              ...(dirtyFields.notes ? { notes: data.notes } : {}),
              ...(dirtyFields.startsAt ? { startsAt: data.startsAt } : {}),
              ...(dirtyFields.attendees
                ? { attendeeContactIds: data.attendees.map((a) => String(a.id)) }
                : {}),
              ...(isUserDirty && selectedUserUid ? { userId: selectedUserUid } : {}),
            },
          },
        });

        if (dirtyFields.attendees) {
          trackContactAttached({
            meetingId: id,
            source: pickerSource,
            contactIds: data.attendees.map((a) => a.id),
            autoPickerOpened: initialPickerVisible,
          });
        }

        showGlobalAlert({
          text: t('meetings.detail.saved', { defaultValue: 'Meeting saved' }),
          variant: 'success',
        });
        if (onSaved) {
          onSaved();
          return;
        }
        router.back();
      } catch {
        showGlobalAlert({
          text: t('meetings.detail.saveFailed', { defaultValue: 'Failed to save meeting' }),
          variant: 'error',
        });
      }
    },
    [
      id,
      dirtyFields,
      initialPickerVisible,
      isUserDirty,
      memoMaxLengthMessage,
      onSaved,
      pickerSource,
      selectedUserUid,
      setError,
      showGlobalAlert,
      t,
      trackContactAttached,
      updateMeeting,
      router,
    ],
  );

  const handleSave = useCallback(() => {
    const notes = getValues('notes');
    if (notes.length > MAX_MEETING_MANUAL_MEMO_LENGTH) {
      setError('notes', { type: 'maxLength', message: memoMaxLengthMessage });
      return;
    }

    if (!isFormDirty) {
      if (onSaved) {
        onSaved();
        return;
      }
      router.back();
      return;
    }

    void handleSubmit(onSubmit)();
  }, [
    getValues,
    handleSubmit,
    isFormDirty,
    memoMaxLengthMessage,
    onSaved,
    onSubmit,
    router,
    setError,
  ]);

  useLayoutEffect(() => {
    if (!saveHandlerRef) {
      return;
    }

    saveHandlerRef.current = handleSave;
  }, [handleSave, saveHandlerRef]);

  const handleOpenPicker = useCallback(() => {
    setPickerAutoOpened(false);
    setPickerVisible(true);
    trackContactPickerShown({ meetingId: id, source: pickerSource, recordingSessionId });
  }, [id, pickerSource, recordingSessionId, trackContactPickerShown]);

  const handleOpenDateTimePicker = useCallback(() => {
    setDateTimePickerVisible(true);
  }, []);

  const trackPickerClosed = useCallback(
    (closeAction: ContactPickerCloseAction, contacts: readonly PickerSelectedContact[]) => {
      trackContactPickerClosed({
        meetingId: id,
        source: pickerSource,
        recordingSessionId,
        closeAction,
        contactIds: contacts.map((contact) => contact.id),
      });
    },
    [id, pickerSource, recordingSessionId, trackContactPickerClosed],
  );

  const handleDismissPicker = useCallback(
    (closeAction: ContactPickerCloseAction, contacts: readonly PickerSelectedContact[]) => {
      trackPickerClosed(closeAction, contacts);
      setPickerVisible(false);
    },
    [trackPickerClosed],
  );

  const handleSkipPicker = useCallback(
    (contacts: readonly PickerSelectedContact[]) => {
      // Report the label the rep saw: Skip when the modal auto-opened, Cancel otherwise.
      trackPickerClosed(pickerAutoOpened ? 'skip' : 'cancel', contacts);
      setPickerVisible(false);
    },
    [pickerAutoOpened, trackPickerClosed],
  );

  const handleDismissDateTimePicker = useCallback(() => {
    setDateTimePickerVisible(false);
  }, []);

  const handleConfirmPicker = useCallback(
    (contacts: readonly PickerSelectedContact[]) => {
      // Fires at the tap; deal_meeting_contact_attached still fires only after the server save.
      trackPickerClosed('attach_contact', contacts);
      setValue('attendees', contacts, { shouldDirty: true });
      setPickerVisible(false);
    },
    [setValue, trackPickerClosed],
  );

  const handleRemoveAttendee = useCallback(
    (contactId: number) => {
      const current = getValues('attendees');
      const filtered = current.filter((a) => a.id !== contactId);
      setValue('attendees', filtered, { shouldDirty: true });
    },
    [getValues, setValue],
  );

  const handleConfirmDateTime = useCallback(
    (dateTime: string) => {
      setValue('startsAt', dateTime, { shouldDirty: true });
      setDateTimePickerVisible(false);
    },
    [setValue],
  );

  const handleOpenSummarySheet = useCallback(() => {
    setSummarySheetVisible(true);
  }, []);

  const handleDismissSummarySheet = useCallback(() => {
    setSummarySheetVisible(false);
  }, []);

  const handleCopySummary = useCallback(() => {
    void Clipboard.setStringAsync(summaryText).then(() => {
      showGlobalAlert({
        text: t('meetings.detail.copiedToClipboard', { defaultValue: 'Copied to clipboard' }),
        variant: 'success',
      });
    });
  }, [summaryText, showGlobalAlert, t]);

  const handleTurnOnWifi = useCallback(() => {
    const action =
      Platform.OS === 'android'
        ? Linking.sendIntent('android.settings.WIFI_SETTINGS')
        : Linking.openURL('App-Prefs:WIFI');
    void action;
  }, []);

  const handleChangeUploadSettings = useCallback(() => {
    router.push({ pathname: '/settings', params: { view: 'network' } });
  }, [router]);

  const wifiActions: ActionLinkItem[] = useMemo(
    () => [
      {
        label: t('meetings.turnOnWifi', { defaultValue: 'Turn on Wifi' }),
        onPress: handleTurnOnWifi,
      },
      {
        label: t('meetings.changeUploadSettings', { defaultValue: 'Change upload settings' }),
        onPress: handleChangeUploadSettings,
      },
    ],
    [t, handleTurnOnWifi, handleChangeUploadSettings],
  );

  return (
    <>
      <ScrollView
        contentContainerStyle={styles.contentContainer}
        contentInsetAdjustmentBehavior="automatic"
        refreshControl={
          <RefreshControl
            colors={[theme.colors.iconNdPrimary]}
            refreshing={refreshing}
            tintColor={theme.colors.iconNdPrimary}
            onRefresh={onRefresh}
          />
        }
        style={styles.container}
        testID="meeting-detail-scroll-view">
        {loading && !data ? (
          <LoadingState />
        ) : error || !meeting ? (
          <ErrorState
            message={t('meetings.detail.loadError', {
              defaultValue: 'Failed to load meeting details',
            })}
          />
        ) : (
          <>
            <Controller
              key={`meeting-title-${meetingTitleSyncKey || id}`}
              control={control}
              name="title"
              render={({ field: { onChange, value } }) => (
                <Pressable
                  onHoverIn={() => setHoveredField('title')}
                  onHoverOut={() => setHoveredField(null)}>
                  <AppTextInput
                    label={t('meetings.detail.titleField', { defaultValue: 'Title' })}
                    mode="flat"
                    style={styles.textInput}
                    testID="meeting-title-input"
                    maxLength={MAX_MEETING_TITLE_LENGTH}
                    value={value}
                    onChangeText={(text) => onChange(text.slice(0, MAX_MEETING_TITLE_LENGTH))}
                    activeUnderlineColor={unisonColors.borderNdStronger}
                    underlineColor={
                      hoveredField === 'title' ? unisonColors.borderNdStronger : undefined
                    }
                    theme={
                      hoveredField === 'title'
                        ? { colors: { onSurfaceVariant: unisonColors.textNdPrimary } }
                        : undefined
                    }
                  />
                </Pressable>
              )}
            />

            <TouchableRipple
              onPress={handleOpenDateTimePicker}
              onHoverIn={() => setHoveredField('datetime')}
              onHoverOut={() => setHoveredField(null)}
              rippleColor={rippleOnSurface}
              style={styles.fieldTouchable}
              testID="meeting-datetime-pressable">
              <AppTextInput
                label={t('meetings.detail.dateTime', { defaultValue: 'Date time' })}
                mode="flat"
                right={<AppTextInput.Icon icon="chevron-down" />}
                style={styles.textInput}
                testID="meeting-datetime-input"
                value={formattedDate}
                editable={false}
                pointerEvents="none"
                activeUnderlineColor={unisonColors.borderNdStronger}
                underlineColor={
                  hoveredField === 'datetime' ? unisonColors.borderNdStronger : undefined
                }
                theme={
                  hoveredField === 'datetime'
                    ? { colors: { onSurfaceVariant: unisonColors.textNdPrimary } }
                    : undefined
                }
              />
            </TouchableRipple>

            <TouchableRipple
              onPress={handleOpenPicker}
              onHoverIn={() => setHoveredField('attendees')}
              onHoverOut={() => setHoveredField(null)}
              rippleColor={rippleOnSurface}
              style={styles.fieldTouchable}
              testID="meeting-attendees-pressable">
              {localAttendees.length > 0 ? (
                <>
                  <View style={styles.attendeesFieldContainer}>
                    <View style={styles.attendeesFieldContent}>
                      <View style={styles.attendeesFieldBody}>
                        <Text style={styles.attendeesLabel}>
                          {t('meetings.detail.attendees', { defaultValue: 'Attendees' })}
                        </Text>
                        <View style={styles.attendeesChips}>
                          {localAttendees.map((attendee) => (
                            <AttendeeChip
                              key={attendee.id}
                              attendee={attendee}
                              onRemove={handleRemoveAttendee}
                            />
                          ))}
                        </View>
                      </View>
                      <MaterialCommunityIcons
                        name="chevron-down"
                        size={24}
                        color={unisonColors.textNdSecondary}
                        style={styles.attendeesFieldIcon}
                      />
                    </View>
                  </View>
                  <View style={styles.attendeesFieldUnderline} />
                </>
              ) : (
                <AppTextInput
                  label={t('meetings.detail.attendees', { defaultValue: 'Attendees' })}
                  mode="flat"
                  right={<AppTextInput.Icon icon="chevron-down" onPress={handleOpenPicker} />}
                  style={styles.textInput}
                  testID="meeting-attendees-input"
                  value={t('meetings.detail.attendeesPlaceholder', {
                    defaultValue: 'Select one or more contact',
                  })}
                  textColor={unisonColors.textNdMuted}
                  editable={false}
                  pointerEvents="none"
                  activeUnderlineColor={unisonColors.borderNdStronger}
                  underlineColor={
                    hoveredField === 'attendees' ? unisonColors.borderNdStronger : undefined
                  }
                  theme={
                    hoveredField === 'attendees'
                      ? { colors: { onSurfaceVariant: unisonColors.textNdPrimary } }
                      : undefined
                  }
                />
              )}
            </TouchableRipple>

            <TouchableRipple
              onPress={() => setUserPickerVisible(true)}
              onHoverIn={() => setHoveredField('personInCharge')}
              onHoverOut={() => setHoveredField(null)}
              rippleColor={rippleOnSurface}
              style={styles.fieldTouchable}
              testID="meeting-person-in-charge-pressable">
              <AppTextInput
                label={t('meetings.detail.personInCharge', { defaultValue: 'Person in charge' })}
                mode="flat"
                right={
                  <AppTextInput.Icon
                    icon="chevron-down"
                    onPress={() => setUserPickerVisible(true)}
                  />
                }
                style={styles.textInput}
                testID="meeting-person-in-charge-input"
                value={selectedUserName}
                editable={false}
                pointerEvents="none"
                activeUnderlineColor={unisonColors.borderNdStronger}
                underlineColor={
                  hoveredField === 'personInCharge' ? unisonColors.borderNdStronger : undefined
                }
                theme={
                  hoveredField === 'personInCharge'
                    ? { colors: { onSurfaceVariant: unisonColors.textNdPrimary } }
                    : undefined
                }
              />
            </TouchableRipple>

            <View testID="meeting-summary-input">
              <View
                style={styles.summaryContainer}
                testID={showRecordingPrompt ? 'meeting-recording-start-cta' : undefined}>
                <View style={styles.summaryContent}>
                  <Text style={styles.fieldLabel}>
                    {t('meetings.detail.summary', { defaultValue: 'AI generated Strategy Note' })}
                  </Text>
                  {showRecordingPrompt ? (
                    <MeetingRecordingPrompt meetingId={id} />
                  ) : (
                    <Text
                      style={[
                        styles.fieldValue,
                        summaryDisplay.isError ? styles.fieldValueError : null,
                      ]}
                      numberOfLines={summaryDisplay.showCompletedLinks ? 6 : undefined}
                      ellipsizeMode={summaryDisplay.showCompletedLinks ? 'tail' : undefined}>
                      {summaryDisplay.text}
                    </Text>
                  )}
                </View>
                {summaryDisplay.showCompletedLinks ? (
                  <View style={styles.linkRow}>
                    <TouchableRipple
                      onPress={handleOpenSummarySheet}
                      rippleColor={rippleOnLink}
                      style={styles.linkTouchable}
                      testID="meeting-summary-see-more">
                      <Text style={styles.linkText}>
                        {t('meetings.detail.seeMore', { defaultValue: 'See more' })}
                      </Text>
                    </TouchableRipple>
                    <TouchableRipple
                      onPress={handleCopySummary}
                      rippleColor={rippleOnLink}
                      style={styles.linkTouchable}
                      testID="meeting-summary-copy-text">
                      <Text style={styles.linkText}>
                        {t('meetings.detail.copyText', { defaultValue: 'Copy Strategy Note' })}
                      </Text>
                    </TouchableRipple>
                  </View>
                ) : null}
                {summaryDisplay.showWifiActions ? (
                  <View style={styles.summaryContent}>
                    <ActionLinkRow actions={wifiActions} testID="meeting-summary-wifi-actions" />
                  </View>
                ) : null}
              </View>
              <View style={styles.divider} />
            </View>

            <Controller
              control={control}
              name="notes"
              render={({ field: { onChange, value }, fieldState }) => (
                <Pressable
                  onHoverIn={() => setHoveredField('memo')}
                  onHoverOut={() => setHoveredField(null)}>
                  <AppTextInput
                    label={t('meetings.detail.memo', { defaultValue: 'Memo' })}
                    mode="flat"
                    multiline
                    maxLength={MAX_MEETING_MANUAL_MEMO_LENGTH}
                    style={styles.textInput}
                    testID="meeting-memo-input"
                    value={value}
                    onChangeText={(text) => {
                      clearErrors('notes');
                      onChange(text.slice(0, MAX_MEETING_MANUAL_MEMO_LENGTH));
                    }}
                    error={Boolean(fieldState.error)}
                    activeUnderlineColor={unisonColors.borderNdStronger}
                    underlineColor={
                      hoveredField === 'memo' ? unisonColors.borderNdStronger : undefined
                    }
                    theme={
                      hoveredField === 'memo'
                        ? { colors: { onSurfaceVariant: unisonColors.textNdPrimary } }
                        : undefined
                    }
                  />
                  <HelperText
                    padding="normal"
                    type="error"
                    visible={Boolean(fieldState.error)}
                    testID="meeting-memo-error-helper">
                    {fieldState.error?.message ?? ' '}
                  </HelperText>
                </Pressable>
              )}
            />
          </>
        )}
      </ScrollView>

      {pickerVisible ? (
        <ContactPickerDialog
          onDismiss={handleDismissPicker}
          onConfirm={handleConfirmPicker}
          onSkip={handleSkipPicker}
          maxSelection={2}
          initialSelectedContacts={localAttendees}
          confirmLabel={t('recording.contactPicker.confirm', {
            defaultValue: 'Attach attendees',
          })}
          skipLabel={
            pickerAutoOpened
              ? t('common.skip', { defaultValue: 'Skip' })
              : t('common.cancel', { defaultValue: 'Cancel' })
          }
          title={t('meetings.detail.contactPickerTitle', {
            defaultValue: 'Who were you in this meeting with?',
          })}
        />
      ) : null}

      {dateTimePickerVisible ? (
        <MeetingDateTimePickerDialog
          visible={dateTimePickerVisible}
          initialDateTime={localStartsAt}
          locale={locale}
          onConfirm={handleConfirmDateTime}
          onDismiss={handleDismissDateTimePicker}
        />
      ) : null}

      <MeetingSummarySheet
        visible={summarySheetVisible}
        title={t('meetings.detail.summary', { defaultValue: 'AI generated Strategy Note' })}
        summary={summaryText}
        onDismiss={handleDismissSummarySheet}
      />
      <UserPickerSheet
        visible={userPickerVisible}
        title={t('meetings.detail.selectPersonInCharge', {
          defaultValue: 'Select a person in charge',
        })}
        currentUserId={selectedUserId}
        users={userOptions}
        loading={usersLoading}
        showIcons={false}
        onSelect={handleSelectUser}
        onDismiss={() => setUserPickerVisible(false)}
      />
    </>
  );
};
